// src/pages/organization/team/ShowTeam.js

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useMainPageTitle } from '../../../contexts/PageContext';
import { getGenderDisplay } from '../../../components/organization/team/GetGenderDisplay';
import TabComponent from '../../../components/common/TabComponent';
import TeamOverviewTab from '../../../components/organization/team/TeamOverviewTab';
import TeamEventsTab from '../../../components/organization/team/TeamEventsTab';
import PlayerJoinRequestsTab from '../../../components/organization/team/PlayerJoinRequestsTab';
import ProfileImage from '../../../components/profileImage';
import DeleteConfirmationDialog from '../../../components/common/DeleteConfirmationDialog';

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";


const ShowTeam = () => {
  const { t } = useTranslation();
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const [teamData, setTeamData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  // Tabs configuration
  const tabs = [
    { id: 'overview', label: t("teams.overview") },
    { id: 'events', label: t("navigation.events") },
    { id: 'requests', label: t("teams.joinRequests"), badge: pendingRequestsCount > 0 ? pendingRequestsCount : null }
  ];

  const fetchTeamData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const url = new URL(`${API_URL}/team/show-team-by-team-id`);
      url.searchParams.append('teamId', teamId);

      console.log('Making request to:', url.toString());

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        // Try to get the error message from the response
        let errorMessage = `Server error (${response.status})`;
        
        try {
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            console.error('Server error response:', errorData);
            errorMessage = errorData.message || errorMessage;
          } else {
            const text = await response.text();
            console.error('Server error text:', text);
            errorMessage = text || errorMessage;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }

        switch (response.status) {
          case 400:
            throw new Error(`Bad request: ${errorMessage}`);
          case 401:
            throw new Error('Your session has expired. Please log in again.');
          case 403:
            throw new Error('You do not have permission to view this team.');
          case 404:
            throw new Error('Team not found.');
          case 500:
            throw new Error('The server encountered an error. Please try again later.');
          default:
            throw new Error(`An error occurred: ${errorMessage}`);
        }
      }

      const data = await response.json();
      console.log('Received data:', data);

      if (!data || !data.team) {
        throw new Error('No team data received from server');
      }

      setTeamData(data);
      setPendingRequestsCount(data.team.pendingRequestsCount || 0);
      
      // After loading team data, fetch pending requests count
      
    } catch (err) {
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      
      // Set a user-friendly error message
      setError(err.message || 'Failed to fetch team data');
      
      // If it's an authentication error, redirect to login
      if (err.message.includes('session has expired')) {
        localStorage.clear(); // Clear all stored data
        navigate('/signin');
      }
    } finally {
      setLoading(false);
    }
  }, [teamId, navigate]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  // Update page title only after team data is loaded
  useEffect(() => {
    if (teamData && teamData.team) {
      updatePageTitle(teamData.team.name, teamData.team.uniqueName);
    }
  }, [teamData, updatePageTitle]);

  const handleDeleteTeam = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_URL}/team/delete-team`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ teamId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete team');
      }

      // Navigate back to teams list after successful deletion
      navigate('/organization/team/all', { 
        state: { message: `Team "${teamData.team.name}" has been deleted successfully` } 
      });
    } catch (err) {
      console.error('Error deleting team:', err);
      setDeleteError(err.message || 'Failed to delete team');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <TeamOverviewTab team={teamData.team} teamMembers={teamData.teamMembers || []} />;
      case 'events':
        return <TeamEventsTab teamId={teamId} />;
      case 'requests':
        return <PlayerJoinRequestsTab teamId={teamId} onRequestProcessed={fetchTeamData} />;
      default:
        return <TeamOverviewTab team={teamData.team} teamMembers={teamData.teamMembers || []} />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F15213]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
            <div className="mt-4 flex gap-4">
              <button
                onClick={() => fetchTeamData()}
                className="bg-red-100 text-red-700 px-4 py-2 rounded hover:bg-red-200 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/organization/team/all')}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 transition-colors"
              >
                Back to Teams
              </button>
              {error.includes('session has expired') && (
                <button
                  onClick={() => navigate('/signin')}
                  className="bg-blue-100 text-blue-700 px-4 py-2 rounded hover:bg-blue-200 transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!teamData || !teamData.team) {
    return (
      <div className="min-h-screen p-8">
        <div className="text-center text-gray-500">
          Team not found
        </div>
        <button
          onClick={() => navigate('/organization/team/all')}
          className="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 transition-colors"
        >
          Back to Teams
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header with Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">{teamData.team.name}</h1>
            {pendingRequestsCount > 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-200">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                {pendingRequestsCount} pending request{pendingRequestsCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => navigate(`/organization/team/edit/${teamData.team._id}`)}
              className="px-4 py-2 bg-[#F15213] text-white rounded-lg hover:bg-[#d14711] transition-colors flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Edit Team
            </button>
            <button
              onClick={() => navigate(`/organization/team/assign-supervisor/${teamData.team._id}`)}
              className="px-4 py-2 bg-[#F15213] text-white rounded-lg hover:bg-[#d14711] transition-colors flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
              </svg>
              Assign Supervisor
            </button>
            <button
              onClick={() => setShowDeleteConfirmation(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Delete Team
            </button>
          </div>
        </div>

        {/* Enhanced Team Card */}
        <div className="bg-gradient-to-br from-white via-gray-50 to-orange-50 rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
          {/* Decorative Top Border */}
          <div className="h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
          
          <div className="md:flex relative">
            {/* Enhanced Logo Section */}
            <div className="md:flex-shrink-0 relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-red-500 opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
              <ProfileImage
                image={teamData.team.logo}
                alt={teamData.team.name}
                user={teamData.team}
                className="h-56 w-full object-cover md:w-56 group-hover:scale-105 transition-transform duration-500"
              />
              {/* Logo Overlay */}
            </div>

            {/* Enhanced Content Section */}
            <div className="flex-1 p-8 bg-white/80 backdrop-blur-sm">
              {/* Team Handle */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-r from-orange-100 to-red-100 rounded-lg">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <p className="text-lg font-semibold text-gray-700 bg-gradient-to-r from-gray-700 to-gray-600 bg-clip-text">@{teamData.team.uniqueName}</p>
                </div>
                
                {/* Team Statistics */}
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{teamData.statistics?.totalMembers || 0}</div>
                    <div className="text-xs text-gray-500 font-medium">Members</div>
                  </div>
                </div>
              </div>

              {/* Enhanced Badges */}
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full text-sm font-medium shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                  {teamData.team.sport}
                </span>
                <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full text-sm font-medium shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  {teamData.team.type}
                </span>
                <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-full text-sm font-medium shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  {getGenderDisplay(teamData.team.gender)}
                </span>
                
              </div>

              {/* Description */}
              {teamData.team.description && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
                    </svg>
                    About Team
                  </h4>
                  <p className="text-gray-600 leading-relaxed">{teamData.team.description}</p>
                </div>
              )}

              {/* Team Creation Info */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 4v6a2 2 0 002 2h4a2 2 0 002-2v-6m-6 0h8" />
                    </svg>
                    <span>Created {new Date(teamData.team.createdAt).toLocaleDateString()}</span>
                  </div>
                  {teamData.team.organizationDetails && (
                    <div className="flex items-center space-x-2">
                      <ProfileImage
                        image={teamData.team.organizationDetails.profilePicture}
                        alt={teamData.team.organizationDetails.name}
                        className="w-6 h-6 rounded-full object-cover border border-gray-200"
                        user={teamData.team.organizationDetails}
                      />
                      <span>by {teamData.team.organizationDetails.name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8">
          <TabComponent 
            tabs={tabs} 
            activeTab={activeTab} 
            onTabChange={setActiveTab} 
          />
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDeleteTeam}
          title="Delete Team"
          message={`Are you sure you want to delete the team "${teamData.team.name}"? This action cannot be undone.`}
          isLoading={deleteLoading}
          error={deleteError}
          confirmButtonText="Delete Team"
        />
      </div>
    </div>
  );
};

export default ShowTeam;
